import { Field } from '@atlaskit/form';
import { Box, Stack, Text, xcss } from '@atlaskit/primitives';
import React, { type ReactNode } from 'react';
import { useIntl } from 'react-intl-next';

import { messages } from '../messages.ts';

export type DefaultValueInputSlotProps = {
  ariaDescribedBy: string | undefined;
  ariaLabel: string;
  inputId: string;
  placeholder: string;
};

type DefaultValueInputProps = {
  children: (slotProps: DefaultValueInputSlotProps) => ReactNode;
  hasValue?: boolean;
  placeholder?: string;
  shouldForwardLabelClick?: boolean;
  testId?: string;
};

const inputWrapperStyles = xcss({
  width: '100%'
});

const DefaultValueInput = (props: DefaultValueInputProps) => {
  const { formatMessage } = useIntl();
  const inputId = 'default-value-input';
  const helperId = 'default-value-input-helper';
  const label = formatMessage(messages.defaultValueInputLabel);
  const helperText = formatMessage(messages.defaultValueInputHelper);
  const placeholder =
    props.placeholder ?? formatMessage(messages.defaultValueInputPlaceholder);
  const hasValue = props.hasValue ?? false;
  const ariaDescribedBy = hasValue ? helperId : undefined;

  return (
    <Stack testId={props.testId} space="space.050">
      <Field
        id={props.shouldForwardLabelClick === false ? undefined : inputId}
        label={
          <Text size="small" weight="bold" color="color.text.subtle">
            {label}
          </Text>
        }
        name="default-value-input-field"
      >
        {({ fieldProps }) => {
          const slotProps: DefaultValueInputSlotProps = {
            ariaDescribedBy,
            ariaLabel: label,
            inputId: props.shouldForwardLabelClick === false ? inputId : fieldProps.id,
            placeholder
          };

          return <Box xcss={inputWrapperStyles}>{props.children(slotProps)}</Box>;
        }}
      </Field>
      {hasValue && (
        <Text id={helperId} size="small" color="color.text.subtlest">
          {helperText}
        </Text>
      )}
    </Stack>
  );
};

export default DefaultValueInput;
